import { getThemeClass, getThemeComponentGroup } from "@/shared/lib/themeComponentClasses";
import type { ButtonProps } from "@/shared/types";
import clsx from "clsx";

export function Button({
  className,
  children,
  disabled,
  isLoading = false,
  loadingText,
  variant = "primary",
  size = "md",
  type = "button",
  ...props
}: ButtonProps) {
  const buttonClasses = getThemeComponentGroup("button");
  const isDisabled = disabled || isLoading;
  const spinnerClassName =
    variant === "primary"
      ? getThemeClass(
          buttonClasses.solidTextColor,
          "border-theme-text-inverse/30 border-t-theme-text-inverse"
        )
      : getThemeClass(buttonClasses.outlineTextColor, "border-theme-text/20 border-t-theme-text");

  return (
    <button
      type={type}
      className={clsx(
        "inline-flex items-center justify-center font-semibold transition focus:outline-none disabled:cursor-not-allowed disabled:opacity-50",
        size === "sm" && "min-h-8 px-4 text-sm",
        size === "md" && "min-h-9 px-5 text-sm",
        size === "lg" && "min-h-11 px-8 text-base",
        buttonClasses.base,
        "rounded-full",
        variant === "primary"
          ? clsx(
              getThemeClass(buttonClasses.solidBgColor, "bg-theme-primary"),
              getThemeClass(buttonClasses.solidTextColor, "text-theme-text-inverse"),
              "hover:bg-theme-primary-hover",
              buttonClasses.solid
            )
          : clsx(
              "border",
              getThemeClass(buttonClasses.outlineBorderColor, "border-theme-border-strong"),
              getThemeClass(buttonClasses.outlineTextColor, "text-theme-text"),
              "hover:bg-theme-surface-soft",
              buttonClasses.outline
            ),
        className
      )}
      disabled={isDisabled}
      aria-busy={isLoading}
      {...props}
    >
      {isLoading ? (
        <span className="flex items-center justify-center gap-2">
          <span className={clsx("h-4 w-4 animate-spin rounded-full border-2", spinnerClassName)} />
          <span>{loadingText || children}</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
}

export default Button;
